// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — markData.js   ·   THE MARK, data layer
// ────────────────────────────────────────────────────────────────────
// THE MARK is the player. One login, one identity, one card — carried
// unchanged into every title on the terror-tcg project. It is not a
// Terror thing, not a Pierre thing, and it is never duplicated per game:
// it reads the shared soul at profiles/{uid} and nothing else.
//
// At a glance it is an ID. Snoop and it opens into deeds, records,
// wardrobe, and whatever the player has for sale. The Codex still owns
// the nitty-gritty of any ONE game; the Mark owns the person.
//
// ── THE ONE NEW NODE ─────────────────────────────────────────────────
//   profiles/{uid}/mark/…   ← owner-write, authed-read. The self-authored
//   layer: tagline, accent, links, pinned deeds, storefront. Everything
//   ELSE on the Mark is derived from nodes that already existed — nothing
//   is copied, nothing is mirrored, nothing can drift.
//
// ── LAWS OBSERVED ────────────────────────────────────────────────────
//   • No new top-level node. `mark` sits inside the existing profile.
//   • Owner writes only. Every writer here hard-checks auth.uid === uid
//     client-side, on top of the rules seal. Defence in depth.
//   • No currency writer. This file cannot move a single Divine Crest,
//     by construction — there is no currency write path in it at all.
//   • Append-only where earned. `mark` holds only self-authored cosmetic
//     text; deeds, records and crests are read from their real homes.
//   • THE MARK CODE is derived, never stored. It cannot be forged into
//     the database because the database does not hold it.
//
// ── NAME COLLISION, DELIBERATE ───────────────────────────────────────
//   `collection/{cardId}/migrations` was historically annotated "THE MARK"
//   (a card's provenance trail). That is now THE CARD'S MARK — a card's
//   travel history. THE MARK, unqualified, means the player. Same idea
//   applied one level up: a permanent, append-only record of where a
//   thing has been. Nothing about the card node changes.
// ════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from "react";
import { ref, get, set, update, onValue } from "firebase/database";   // `off` retired 2026-09-05 — see the leak notes below
import { db, auth } from "./firebase";

export const MARK_SCHEMA_VERSION = "1.0";

// ════════════════════════════════════════════════════════════════════
// MK — THE MARK'S CHROME
// ════════════════════════════════════════════════════════════════════
// Moved down here from Mark.js (2026-08-21) so the friends list can
// wear it. Mark.js re-exports MK, so every existing importer is
// unchanged.
//
// WHY IT LIVES IN THE DATA FILE. Mark.js needs useAchievements,
// achievements, SpriteManifest and AchievementBadgeBook — a stack only
// four titles carry. markData.js needs React and Firebase and nothing
// else, so it goes everywhere. THE MARK IS THE PLAYER (§3.7b): if the
// player's identity is rendered in a different palette depending on
// which room they are standing in, it is not one identity. The tokens
// have to travel further than the card does.
export const MK = {
  void:     "#06060a",
  obsidian: "#0b0910",
  slab:     "#12101a",
  slabHi:   "#1a1724",
  edge:     "#241f31",
  edgeHot:  "#3a3050",
  bone:     "#ece6da",
  ash:      "#8b8496",
  smoke:    "#59536a",
  gold:     "#d4a03c",
  goldHi:   "#f6dfa0",
  goldLo:   "#7a5c1f",
  blood:    "#c01427",
  violet:   "#7c3aed",
  verd:     "#5fa79f",
  // ── ⭐ THE PLATED TEXT PALETTE ──────────────────────────────────
  // caP'n, 2026-08-25: "no mythical rainbow effect."
  //
  // He is right, and the first fix over-corrected. A foil is a
  // LIGHTENING effect, so making it legible by scrimming it is a
  // straight trade: every point of contrast bought is a point of shine
  // sold, and at the floor the last pass needed, the plate was dead.
  //
  // ⭐ THE SCRIM WAS NEVER THE ONLY LEVER. THE TEXT IS THE OTHER ONE,
  // AND IT IS THE CHEAPER OF THE TWO.
  //
  // `ash` is a mid-grey: it needs a genuinely dark background to clear
  // 4.5:1, so it forces a heavy scrim all by itself. Lift the text a
  // few stops and the SAME contrast ratio survives a background three
  // times brighter — which is three times more foil, for free.
  //
  // So: these are the tokens for text that sits ON A PLATE. Same roles,
  // same relationships, lifted. Unplated chrome elsewhere in the Empire
  // keeps the originals; nothing else changes.
  //
  // ⚠ THE NUMBERS ARE OUTPUTS, NOT TASTE. They come from
  // `gamemaster/tools/mark-contrast.mjs`, which solves them against the
  // brightest a foil can ever go. Change one and re-run it.
  ashP:     "#cfc9da",   // body copy      (was ash    #8b8496)
  smokeP:   "#a8a1b8",   // micro labels   (was smoke  #59536a)
  verdP:    "#8fd4cc",   // ok / live      (was verd   #5fa79f)

  display:  "'Bodoni Moda', 'Didot', Georgia, serif",
  mono:     "'JetBrains Mono', 'Courier New', monospace",
  sans:     "'Barlow Condensed', 'Helvetica Neue', sans-serif",
};

// The cut corner every Mark surface shares — a nameplate stamped, not
// a rounded card. One constant so the friends roster and the Mark card
// cannot drift apart by a pixel.
// THE CUT. A Mark surface is STAMPED, not rounded — and the size of the
// cut scales with the surface. 7px on a chip, 14 on a panel, 18 on the
// card. Blowing the chip's 7px up to a 330px panel reads as a rendering
// artifact rather than a stamp, which is exactly what the first draft
// of the friends panel did.
export const mkCut = (n = 7) =>
  `polygon(0 0, calc(100% - ${n}px) 0, 100% ${n}px, 100% 100%, ${n}px 100%, 0 calc(100% - ${n}px))`;
export const MK_CUT = mkCut(7);

// ── THE CHROME ATOMS ────────────────────────────────────────────────
// Moved here from Mark.js (2026-08-21) alongside MK, and for the same
// reason: any surface that renders a player must be able to wear them,
// including the ones that cannot carry Mark.js's achievement stack.
// ONE definition. A second copy of Rule or Sheen is how two screens
// stop looking like one product.

let _mkInjected = false;
export function injectMarkChrome() {
  if (_mkInjected || typeof document === "undefined") return;
  _mkInjected = true;
  if (!document.getElementById("mk-fonts")) {
    const l = document.createElement("link");
    l.id = "mk-fonts";
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,wght@0,400;0,600;0,800;1,400&family=JetBrains+Mono:wght@400;600;700&family=Barlow+Condensed:wght@400;600;700&display=swap";
    document.head.appendChild(l);
  }
  if (!document.getElementById("mk-keys")) {
    const st = document.createElement("style");
    st.id = "mk-keys";
    st.textContent = `
@keyframes mkSheen { 0% { transform: translateX(-120%) } 100% { transform: translateX(220%) } }
@keyframes mkRise  { from { opacity:0; transform: translateY(10px) scale(.985) } to { opacity:1; transform:none } }
@keyframes mkFade  { from { opacity:0 } to { opacity:1 } }
@keyframes mkPulse { 0%,100% { opacity:.35 } 50% { opacity:.9 } }
@keyframes mkDrop  { from { opacity:0; transform: translateY(-10px) } to { opacity:1; transform:none } }
@keyframes mkBeat  { 0%,100% { opacity:1 } 50% { opacity:.6 } }
.mk-scroll::-webkit-scrollbar { width:6px }
.mk-scroll::-webkit-scrollbar-thumb { background:${MK.edgeHot}; border-radius:3px }
.mk-scroll::-webkit-scrollbar-track { background:transparent }
.mk-tab:hover { color:${MK.bone} !important }
.mk-link:hover { border-color:${MK.gold} !important; color:${MK.bone} !important }
`;
    document.head.appendChild(st);
  }
}

// THE micro-label. One spec, mono, .28em, uppercase. Every kicker in
// the Empire is this or it is a bug — the friends panel shipped four
// different letter-spacings before this existed.
export const Kicker = ({ children, tint = MK.smoke, size = 9 }) => (
  <div style={{
    fontFamily: MK.mono, fontSize: size, letterSpacing: ".28em",
    textTransform: "uppercase", color: tint,
  }}>{children}</div>
);

// A divider that fades at both ends. A flat 1px line is a web form.
export const Rule = ({ tint = MK.edge }) => (
  <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${tint} 18%, ${tint} 82%, transparent)` }} />
);

// The gilded sheen that sweeps a slab once on mount. One element, one
// animation, pointer-events off — it can never eat a click.
export const Sheen = ({ tint }) => (
  <div style={{
    position: "absolute", inset: 0, overflow: "hidden",
    pointerEvents: "none", borderRadius: "inherit",
  }}>
    <div style={{
      position: "absolute", top: 0, bottom: 0, width: "45%",
      background: `linear-gradient(105deg, transparent 40%, ${tint}22 48%, #ffffff18 51%, ${tint}22 54%, transparent 62%)`,
      animation: "mkSheen 2.4s cubic-bezier(.22,.61,.36,1) .15s 1 both",
    }} />
  </div>
);

// The 2px accent bar across the top of a slab — the other half of what
// makes a Mark surface read as a CARD rather than a dropdown.
export const TopRule = ({ accent }) => (
  <div style={{
    position: "absolute", left: 0, right: 0, top: 0, height: 2, pointerEvents: "none",
    background: `linear-gradient(90deg, transparent, ${accent.hex} 45%, ${accent.hi} 55%, transparent)`,
    opacity: .8,
  }} />
);

// ════════════════════════════════════════════════════════════════════
// ⭐ THE MARKLINE — the barcode, brought back and made the player's
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-25: "Terror had a cool barcode thing on the Mark — we
// should definitely keep that."
//
// He is right, and it was always THE MARK's signature. Terror's
// `TheMark.js` carries one for a BEING; this is the same idea one level
// up, for the PERSON: a deterministic strip of bars generated from the
// player's Mark Code. Same player → same bars, in every game, forever.
// Decorative, but it READS AS PROCESSING — which is the whole point of
// a thing the Empire stamps on you whether you like it or not.
//
// It lives here, beside MK and Kicker and Rule, for the reason stated
// over those: markData travels to every title, Mark.js does not. THE
// MARK IS THE PLAYER — a barcode that appears in one game and not
// another is not one identity. (COHERENCY LAW, §2.22 Law 36.)
//
// ── caP'n's two open ideas, and the seam that is already cut for them
// "maybe the soundwaves of the name — or eventually an alien language
// and say it like that."  Both are the SAME component with a different
// `mode`, which is why `mode` exists today rather than being retrofitted:
//   • "code"  — the barcode. The default, and what Terror had.
//   • "wave"  — a waveform. Bars mirrored about a centre line, heights
//               driven by the NAME rather than the code, so it reads as
//               the player's name spoken aloud. Built, shipped, one
//               prop away.
//   • "glyph" — the alien language. NOT built. When the Empire has an
//               alphabet, it is a third case here and nothing else in
//               the Empire changes. Do not invent one to fill the gap.
// ════════════════════════════════════════════════════════════════════

// Deterministic, seeded, and NEVER called during render — the bars are
// baked into a useMemo. A PRNG called in a render body advances on every
// re-render and the barcode visibly crawls.
function mlHash(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function mlRand(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function Markline({
  seed = "",
  name = "",
  mode = "code",
  width = "100%",
  height = 22,
  tint = MK.gold,
  tintHi = MK.goldHi,
  opacity = 1,
}) {
  const bars = useMemo(() => {
    const out = [];
    if (mode === "wave") {
      // THE WAVEFORM. Heights come from the NAME, not the code, so it is
      // the player's name you are looking at — evenly spaced, mirrored
      // about the centre line, the way a voice print reads.
      const src = String(name || seed || "?");
      const n = Math.max(18, Math.min(56, src.length * 4));
      const r = mlRand(mlHash("wave::" + src));
      for (let i = 0; i < n; i++) {
        const ch = src.charCodeAt(i % src.length) || 65;
        // the character sets the note; the seeded jitter stops it
        // looking like a sawtooth for a repeated name
        const base = ((ch % 23) / 23) * 0.72 + 0.14;
        const h = Math.max(0.1, Math.min(1, base + (r() - 0.5) * 0.3));
        out.push({ x: (i / n) * 100, w: (100 / n) * 0.55, h, wave: true, op: 0.5 + h * 0.5 });
      }
      return out;
    }
    // THE BARCODE — Terror's, ~44 bars, deterministic widths, heights
    // and opacities.
    const r = mlRand(mlHash("markline::" + String(seed || name || "?")));
    let x = 0;
    while (x < 100) {
      const w = 0.7 + r() * 2.6;
      const gap = 0.5 + r() * 1.6;
      out.push({ x, w, h: r() > 0.28 ? 1 : 0.56, op: 0.45 + r() * 0.55 });
      x += w + gap;
    }
    return out;
  }, [seed, name, mode]);

  return (
    <div style={{ position: "relative", overflow: "hidden", width, opacity }}>
      <svg viewBox="0 0 100 10" preserveAspectRatio="none"
           width="100%" height={height} style={{ display: "block" }}>
        {bars.map((b, i) => (
          <rect
            key={i}
            x={b.x}
            y={b.wave ? (10 - b.h * 10) / 2 : (b.h === 1 ? 0 : 2.2)}
            width={b.w}
            height={b.wave ? b.h * 10 : b.h * 10}
            fill={tint}
            opacity={b.op}
            rx={b.wave ? 0.35 : 0}
          />
        ))}
      </svg>
      {/* one restrained glint — the Empire's scanner passing over you */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `linear-gradient(105deg, transparent 40%, ${tintHi}33 50%, transparent 60%)`,
        animation: "mkSheen 8s ease-in-out infinite",
      }} />
    </div>
  );
}

// ── Paths — one source of truth, never concatenated at a call site ───
export const profilePath = (uid) => `profiles/${uid}`;
export const markPath    = (uid) => `profiles/${uid}/mark`;
export const storePath   = (uid) => `profiles/${uid}/mark/store`;
export const variantPath = (uid) => `profiles/${uid}/mark/variant`;

// ── THE WORN PLATE, for surfaces that do not already hold the Mark ───
// ⚠ A LEAF LISTENER, NEVER THE PROFILE ROOT. `profiles/{uid}` carries
// the whole Terror collection, every achievement and every cosmetic;
// subscribing to it to learn one string means every unrelated write in
// the Empire wakes this surface and synchronously deserialises the lot
// on the main thread. That is the freeze-then-catch-up bug, and a
// friends panel is not worth it. (ie-realtime-perf, Cause 2.)
// ── EQUIP. Owner-gated, one leaf, no currency path in it at all ──────
// Equipping is not SELLING. Cosmetics are equip-anywhere/appear-
// everywhere (§3.5), so any title may re-plate its own player's Mark —
// but only Pierre may VEND one (COHERENCY LAW, §2.22 Law 36). That is
// why this lives here in the vendored kit and `markShop.js` does not.
export async function setWornVariant(uid, variantId) {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false, reason: "not_you" };
  try {
    await update(ref(db, markPath(uid)), { variant: variantId || null });
  } catch (e) {
    return { ok: false, reason: "write_failed" };
  }
  return { ok: true, variantId };
}

// Which plates this player owns, read straight off the profile the Mark
// already holds — no second listener for a list we are already given.
export const OWNED_VARIANT_PREFIX = "mark_variant___";
export function ownedVariantIds(profile) {
  const all = profile?.unlockedCosmetics || {};
  const out = [];
  for (const k of Object.keys(all)) {
    if (k.startsWith(OWNED_VARIANT_PREFIX)) out.push(k.slice(OWNED_VARIANT_PREFIX.length));
  }
  return out;
}

export function useWornVariant(uid) {
  const [id, setId] = useState(null);
  useEffect(() => {
    if (!uid) { setId(null); return; }
    const r = ref(db, variantPath(uid));
    // ⭐ REAL LEAK, FIXED 2026-09-05. `onValue` returns an UNSUBSCRIBE
    // function, not the snapshot callback — and `off(ref, "value", cb)`
    // matches registrations by comparing against the registered
    // snapshotCallback. The unsubscribe function is not that function and
    // carries no .userCallback, so NO registration ever matched and this
    // removed nothing at all. Every mount leaked a live profiles/{uid}
    // listener for the life of the page. Call the unsubscribe.
    const un = onValue(r, (snap) => setId(snap.val() || null));
    return un;
  }, [uid]);
  return id;
}

// ════════════════════════════════════════════════════════════════════
// THE MARK CODE — the public face of a uid
// ════════════════════════════════════════════════════════════════════
// A Firebase uid is a 28-character opaque string. It is not a secret,
// but it is ugly, unmemorable, and it invites people to paste it into
// places a uid should never go. The Mark Code is what a player actually
// reads, says out loud, and searches for: eight Crockford base-32
// characters, deterministic, collision-tolerant, printed as MK-XXXX-XXXX.
//
// DERIVED, NOT STORED — the single most important property here. Because
// it is a pure function of the uid, it cannot be edited, spoofed, bought,
// or written to the database by anyone. Two players cannot hold the same
// displayed identity by writing the same field, because there is no field.
//
// Crockford base-32 drops I, L, O and U on purpose: no 1/I, no 0/O
// confusion when read off a screen, and no accidental words.
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

export const markCode = (uid) => {
  if (!uid) return "MK-••••-••••";
  // FNV-1a, twice, with different offsets → 64 bits of spread from a
  // short string. Not cryptographic and does not need to be: this is a
  // display label, and the uid remains the only identity that decides
  // anything. Cheap, synchronous, no dependency, stable forever.
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < uid.length; i++) {
    const c = uid.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ (c + i), 0x85ebca6b) >>> 0;
  }
  let out = "";
  for (let i = 0; i < 4; i++) { out += CROCKFORD[h1 & 31]; h1 >>>= 5; }
  for (let i = 0; i < 4; i++) { out += CROCKFORD[h2 & 31]; h2 >>>= 5; }
  return `MK-${out.slice(0, 4)}-${out.slice(4)}`;
};

// The seal glyph — a stable per-player mark drawn from the same hash, so
// every player's card carries one small thing nobody else has. Purely
// decorative; carries no authority and gates nothing.
const SEALS = ["◆", "◈", "❖", "✦", "✧", "⬖", "⬗", "⧫", "◇", "❋", "✜", "⟡"];
export const markSeal = (uid) => {
  if (!uid) return "◆";
  let h = 0x811c9dc5;
  for (let i = 0; i < uid.length; i++) h = Math.imul(h ^ uid.charCodeAt(i), 16777619) >>> 0;
  return SEALS[h % SEALS.length];
};

// ════════════════════════════════════════════════════════════════════
// STANDING — the ladder, obeying THE THREE-LEVEL LAW
// ════════════════════════════════════════════════════════════════════
// Ranks are the small unit, levels are what ranks buy, and there are
// exactly three levels. Never a fourth. Renown (already computed from
// the shared achievement registry) is what pours into the pool, so
// Standing needs no new stored number anywhere — it is derived on read
// and can never fall out of sync with the deeds that produced it.
//
// RANKS_PER_LEVEL is a per-title dial, and this is the Empire-wide one.
// Tune it in practice; nothing else has to move when it changes.
export const RANKS_PER_LEVEL = 12;         // ⟵ DIAL
export const MAX_LEVEL = 3;                // ⟵ LAW. Not a dial.
export const RENOWN_PER_RANK = 25;         // ⟵ DIAL

export const STANDING_NAMES = ["UNMARKED", "MARKED", "SIGILED", "SOVEREIGN"];

export const deriveStanding = (renown = 0) => {
  const ranks = Math.floor(Math.max(0, renown) / RENOWN_PER_RANK);
  const capped = Math.min(ranks, RANKS_PER_LEVEL * MAX_LEVEL);
  const level = Math.min(MAX_LEVEL, Math.floor(capped / RANKS_PER_LEVEL));
  const intoLevel = capped - level * RANKS_PER_LEVEL;
  const maxed = level >= MAX_LEVEL;
  return {
    level,                                     // 0..3
    name: STANDING_NAMES[level],
    ranks: capped,
    ranksIntoLevel: maxed ? RANKS_PER_LEVEL : intoLevel,
    ranksPerLevel: RANKS_PER_LEVEL,
    pct: maxed ? 1 : intoLevel / RANKS_PER_LEVEL,
    maxed,
    nextRenown: maxed ? null : (level * RANKS_PER_LEVEL + intoLevel + 1) * RENOWN_PER_RANK,
  };
};

// ════════════════════════════════════════════════════════════════════
// THE MARK SHAPE — what a fresh, never-edited Mark looks like
// ════════════════════════════════════════════════════════════════════
// Every field here is self-authored decoration. Nothing in this object
// gates anything, unlocks anything, or is worth cheating for — which is
// exactly why it is safe for it to be owner-writable with no server in
// the middle. Anything a player could want to LIE about (crests, deeds,
// records, collection) is deliberately not in here.
export const EMPTY_MARK = {
  v: MARK_SCHEMA_VERSION,
  tagline: "",          // one line under the name. Capped on write.
  accent: "gold",       // key into MARK_ACCENTS — tints the whole card
  status: "",           // short "what I'm doing" note, MySpace-style
  pinned: [],           // up to 6 achievement ids surfaced on the glance card
  links: {},            // { label: url } — capped, sanitised on write
  store: { open: false, items: {} },
  updatedAt: 0,
};

// Accents are a closed list here only because they must map to real
// colour tokens the renderer knows. New accent = one row, no engine
// change — the seam is deliberate (THE EXCEPTION LAW: this is the set
// of accents that exist today, not the set that can ever exist).
export const MARK_ACCENTS = {
  gold:   { label: "Gilt",     hex: "#d4a03c", hi: "#f6dfa0" },
  violet: { label: "Voidlight", hex: "#7c3aed", hi: "#a78bfa" },
  sky:    { label: "Tidelight", hex: "#38bdf8", hi: "#7dd3fc" },
  blood:  { label: "Vermillion", hex: "#c01427", hi: "#ff3d52" },
  verd:   { label: "Canon",    hex: "#5fa79f", hi: "#9ed8d1" },
  steel:  { label: "Ashsteel", hex: "#6b7391", hi: "#a6adc4" },
  bone:   { label: "Bone",     hex: "#ece6da", hi: "#ffffff" },
};
export const accentOf = (mark) =>
  MARK_ACCENTS[mark?.accent] || MARK_ACCENTS.gold;

// ── Caps. Enforced here so every writer inherits them ────────────────
export const CAPS = {
  tagline: 90,
  status: 60,
  links: 5,
  linkLabel: 24,
  pinned: 6,
  storeItems: 60,
  itemTitle: 70,
  itemBlurb: 160,
};

const clip = (s, n) => String(s ?? "").slice(0, n);

// Only http(s). Blocks javascript:, data:, and every other scheme that
// turns a profile link into an execution vector on someone else's screen.
export const safeUrl = (u) => {
  const s = String(u ?? "").trim();
  if (!/^https?:\/\//i.test(s)) return "";
  return s.slice(0, 300);
};

// ════════════════════════════════════════════════════════════════════
// READ — hooks. Every Mark surface in every title reads through these,
// so no screen can invent its own path or fall behind.
// ════════════════════════════════════════════════════════════════════

// useMark(uid) — live profile + live mark, normalised and null-safe.
// uid === null subscribes to nothing and returns a safe empty snapshot.
export function useMark(uid) {
  const [profile, setProfile] = useState(null);
  const [mark, setMark] = useState(EMPTY_MARK);
  const [loading, setLoading] = useState(!!uid);

  useEffect(() => {
    if (!uid) { setProfile(null); setMark(EMPTY_MARK); setLoading(false); return; }
    setLoading(true);
    const pRef = ref(db, profilePath(uid));
    const cb = onValue(pRef, (snap) => {
      const p = snap.val() || null;
      setProfile(p);
      setMark({ ...EMPTY_MARK, ...(p?.mark || {}) });
      setLoading(false);
    }, () => setLoading(false));
    // Same leak as useWornVariant above, and this one is the expensive
    // half: it is the hook the friends panel opens for EVERY friend's
    // Mark, so browsing a roster of twenty left twenty live full-profile
    // listeners running until the tab closed.
    return cb;
  }, [uid]);

  // Derived — computed on read, stored nowhere, unforgeable.
  const derived = useMemo(() => {
    const lifetime = profile?.records?.lifetime || {};
    const gamesPlayed = lifetime.gamesPlayed || {};
    return {
      code: markCode(uid),
      seal: markSeal(uid),
      name: profile?.displayName || profile?.name || "UNCLAIMED",
      since: profile?.createdAt || lifetime.firstLogin || null,
      crests: Number(profile?.divineCrests || 0),
      faction: profile?.faction || null,
      gender: profile?.gender === "m" ? "m" : "f",
      companionId: profile?.companionId || "default",
      equipped: profile?.cosmetics?.equipped || profile?.equipped || {},
      title: profile?.cosmetics?.equipped?.title || profile?.equipped?.title || null,
      gamesPlayed,
      gamesTouched: Object.keys(gamesPlayed).length,
      totalSessions: Number(lifetime.totalSessions || 0),
      collectionSize: Object.keys(profile?.collection || {}).length,
      cosmeticsOwned: Object.keys(profile?.cosmetics?.owned || profile?.unlockedCosmetics || {}).length,
      friends: Object.keys(profile?.friends || {}).length,
      storeItems: Object.values(mark?.store?.items || {}),
      storeOpen: !!mark?.store?.open && Object.keys(mark?.store?.items || {}).length > 0,
    };
  }, [uid, profile, mark]);

  return { loading, profile, mark, ...derived };
}

// One-shot read, for non-React callers (a plaza tooltip, a chat hover).
export const getMark = async (uid) => {
  if (!uid) return null;
  const snap = await get(ref(db, profilePath(uid)));
  const p = snap.val();
  if (!p) return null;
  return {
    uid,
    code: markCode(uid),
    seal: markSeal(uid),
    name: p.displayName || p.name || "UNCLAIMED",
    crests: Number(p.divineCrests || 0),
    mark: { ...EMPTY_MARK, ...(p.mark || {}) },
  };
};

// ════════════════════════════════════════════════════════════════════
// WRITE — owner only, and only ever into profiles/{uid}/mark/…
// ════════════════════════════════════════════════════════════════════
// Every function below refuses outright if the caller is not the signed-in
// owner. The rules seal (auth.uid === $uid on profiles/$uid) is the real
// enforcement; this is the second lock, so a mis-wired screen fails loudly
// in our own code instead of silently against Firebase.
const assertOwner = (uid) => {
  const me = auth.currentUser?.uid;
  if (!me) throw new Error("[Mark] not signed in");
  if (me !== uid) throw new Error("[Mark] refusing to write another player's Mark");
  return me;
};

export const saveMarkBasics = async (uid, { tagline, status, accent } = {}) => {
  assertOwner(uid);
  const patch = { v: MARK_SCHEMA_VERSION, updatedAt: Date.now() };
  if (tagline !== undefined) patch.tagline = clip(tagline, CAPS.tagline);
  if (status  !== undefined) patch.status  = clip(status, CAPS.status);
  if (accent  !== undefined) patch.accent  = MARK_ACCENTS[accent] ? accent : "gold";
  await update(ref(db, markPath(uid)), patch);
};

// Pinned deeds are ids only — the achievement itself still lives in
// profiles/{uid}/achievements and is still the only proof of earning it.
// Pinning something you do not hold shows nothing: the renderer resolves
// pins against the real earned map, so a forged pin is inert.
export const savePinned = async (uid, ids = []) => {
  assertOwner(uid);
  const clean = [...new Set(ids.filter(Boolean).map(String))].slice(0, CAPS.pinned);
  await update(ref(db, markPath(uid)), { pinned: clean, updatedAt: Date.now() });
};

export const saveLinks = async (uid, links = {}) => {
  assertOwner(uid);
  const clean = {};
  Object.entries(links).slice(0, CAPS.links).forEach(([label, url]) => {
    const u = safeUrl(url);
    if (u) clean[clip(label, CAPS.linkLabel)] = u;
  });
  await set(ref(db, `${markPath(uid)}/links`), clean);
  await update(ref(db, markPath(uid)), { updatedAt: Date.now() });
};

// ── Storefront ───────────────────────────────────────────────────────
// A listing is a SHOP WINDOW, not a transaction. It says "this exists and
// I want this many Crests for it." Nothing here moves currency, and
// nothing here is trusted as a price at purchase time — when the actual
// vending layer lands it will re-read the listing server-side and settle
// through the crest helper, never through a number the buyer's client
// carried in. Keeping listings decorative is what makes them safe to be
// owner-writable today.
export const upsertListing = async (uid, item) => {
  assertOwner(uid);
  const id = item.id || `it_${Date.now().toString(36)}`;
  const row = {
    id,
    kind: clip(item.kind || "misc", 24),        // "track" | "album" | "book" | "art" | "misc"
    title: clip(item.title || "Untitled", CAPS.itemTitle),
    blurb: clip(item.blurb || "", CAPS.itemBlurb),
    priceDC: Math.max(0, Math.floor(Number(item.priceDC) || 0)),
    cover: safeUrl(item.cover),
    href: safeUrl(item.href),
    addedAt: item.addedAt || Date.now(),
  };
  await set(ref(db, `${storePath(uid)}/items/${id}`), row);
  await update(ref(db, markPath(uid)), { updatedAt: Date.now() });
  return row;
};

export const removeListing = async (uid, id) => {
  assertOwner(uid);
  await set(ref(db, `${storePath(uid)}/items/${id}`), null);
};

export const setStoreOpen = async (uid, open) => {
  assertOwner(uid);
  await update(ref(db, storePath(uid)), { open: !!open });
};

// ── Formatting helpers the card and its chip both use ────────────────
export const fmtNum = (n) => Number(n || 0).toLocaleString();
export const fmtDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
export const fmtSince = (ts) => {
  if (!ts) return "—";
  const days = Math.floor((Date.now() - ts) / 86400000);
  if (days < 1) return "today";
  if (days < 60) return `${days}d`;
  if (days < 730) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
};

export default useMark;
